import React, { useState, useEffect } from 'react';
import { DB } from '../../data/database';
import { CUT_TYPES, ROLES } from '../../data/constants';
import { formatPrice } from '../../utils/currency';
import { useLanguage } from '../../translations/LanguageContext';
import { ImageIcon, PrintIcon, WhatsAppIcon, CheckIcon } from '../Icons';
import JSZip from 'jszip';
import QRBox from '../ui/QRBox';
import { logActivity } from '../../data/notifications';
import centralUpdateStatus, { detectUnitType } from '../../utils/updateUnitStatus';
import PrintLabelModal from '../PrintLabelModal';

import { getYardLocations, getDestinationCountries, resolveTargetZone } from '../../data/constants';
import { PART_TYPES, getPartType, normalizeCutType, resolvePartTypeEntry } from '../../data/partTypes';

const CUT_OPTIONS = PART_TYPES.filter(pt => pt.tier === 'cut');

const STATUS_OPTIONS = [
  { value: 'available',  label: 'Available' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'assigned',   label: 'Assigned to Customer' },
  { value: 'sold',       label: 'Sold' },
];

function DetailModal({ item, type, onClose, currency, canPrice, isCustomer, lang, onUpdate, user, onRequestParts }) {
  const { t } = useLanguage();
  const [status, setStatus]         = useState(item.status);
  const [assignedTo, setAssignedTo] = useState(item.assignedTo || '');
  const [notes, setNotes]           = useState(item.notes || '');
  const [isDirty, setIsDirty]       = useState(false);
  const [activeImg, setActiveImg]   = useState(0);
  const [lightbox, setLightbox]     = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [convertType, setConvertType] = useState(() => {
    if (item.cutType) {
      const n = normalizeCutType(item.cutType);
      if (getPartType(n)) return n;
    }
    const resolved = resolvePartTypeEntry(item);
    return resolved.tier === 'cut' ? resolved.id : 'half_cut';
  });
  const [showPrintLabel, setShowPrintLabel] = useState(false);
  const [destinationYard, setDestinationYard] = useState(item.destination_yard || '');
  const [destinationCountry, setDestinationCountry] = useState(item.destination_country || '');
  const [unitCutType, setUnitCutType] = useState(item.cut_type || '');
  const [partialSellQty, setPartialSellQty] = useState(1);

  const images = item.images || [];
  const auctionSheets = item.auction_sheets || [];
  const [sheetViewer, setSheetViewer] = useState(null);

  // Keyboard navigation (gallery + lightbox)
  useEffect(() => {
    if (images.length === 0) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft')  setActiveImg(i => (i - 1 + images.length) % images.length);
      if (e.key === 'ArrowRight') setActiveImg(i => (i + 1) % images.length);
      if (e.key === 'Escape')     setLightbox(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [images.length]);

  const customers = DB.get('customers') || [];
  const canEdit   = !isCustomer && !!onUpdate;
  const isAdmin    = user?.role === 'admin' || user?.role === 'head_admin';
  const isOperator = user?.role === 'yard_operator';
  const isManager  = user?.role === 'yard_manager';
  const isYardRole = isOperator || isManager;
  const roleCanSeeFinancials = ROLES[user?.role]?.canSeeFinancials !== false;
  const showPrices = canPrice && roleCanSeeFinancials;

  const name = item.make
    ? `${item.make} ${item.model}`
    : item.engineName || item.partName;

  const cutTypeName = CUT_TYPES.find(c => c.id === item.cutType)?.name;

  const openWhatsApp = () => {
    const unitUrl = `${window.location.origin}/?unit=${item.sku}`;
    const message = `Hi, I'm interested in ${name} (${item.sku})\n\nView unit: ${unitUrl}`;
    window.open(`https://wa.me/971565665400?text=${encodeURIComponent(message)}`);
  };

  const handleStatusChange = (val) => {
    setStatus(val);
    if (val !== 'assigned') setAssignedTo('');
    setIsDirty(true);
  };

  const handleSave = () => {
    const updated = {
      ...item,
      status,
      destination_yard: destinationYard || null,
      destination_country: destinationCountry || null,
      cut_type: unitCutType || null,
      assignedTo: status === 'assigned' ? assignedTo : '',
      notes,
    };

    // Fire assignment notification when a unit is newly assigned to a customer
    if (status === 'assigned' && assignedTo && assignedTo !== item.assignedTo) {
      const customer = customers.find(c => c.id === assignedTo);
      const unitName = item.make ? `${item.make} ${item.model}` : item.engineName || item.partName;
      logActivity.unitAssigned(unitName, customer?.name || '', user?.name || 'Staff', assignedTo);
    }

    onUpdate(updated);
    setIsDirty(false);
  };

  return (
    <>
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{name}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* ── Image gallery ── */}
          {images.length > 0 ? (
            <div className="detail-gallery">
              <div className="detail-gallery-main">
                <img
                  src={images[activeImg]}
                  alt={`${name} ${activeImg + 1}`}
                  style={{ cursor: 'zoom-in' }}
                  onClick={() => setLightbox(true)}
                />
                {images.length > 1 && (
                  <>
                    <button className="detail-gallery-nav detail-gallery-prev"
                      onClick={() => setActiveImg(i => (i - 1 + images.length) % images.length)}>‹</button>
                    <button className="detail-gallery-nav detail-gallery-next"
                      onClick={() => setActiveImg(i => (i + 1) % images.length)}>›</button>
                    <div className="detail-gallery-counter">{activeImg + 1} / {images.length}</div>
                  </>
                )}
              </div>
              {images.length > 1 && (
                <div className="detail-gallery-thumbs">
                  {images.map((src, i) => (
                    <div key={i} className={`detail-gallery-thumb${i === activeImg ? ' active' : ''}`} onClick={() => setActiveImg(i)}>
                      <img src={src} alt={`${name} ${i + 1}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="detail-img" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: 'var(--text3)' }}>
              <ImageIcon />
              <div style={{ marginTop: 8 }}>No Image</div>
            </div>
          )}

          {/* ── Auction Sheets (visible to admin, accountant, japan_yard, data_entry, customers) ── */}
          {auctionSheets.length > 0 && !['yard_sales', 'destination'].includes(user?.role) && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                📄 Auction Sheet{auctionSheets.length > 1 ? 's' : ''} <span style={{ fontWeight: 400, color: 'var(--text3)' }}>({auctionSheets.length})</span>
              </div>
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
                {auctionSheets.map((src, i) => (
                  <div key={i} style={{ flexShrink: 0, width: 120, height: 90, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', cursor: 'pointer', position: 'relative' }}
                    onClick={() => setSheetViewer(i)}>
                    <img src={src} alt={`Auction sheet ${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', top: 0, left: 0, background: 'var(--blue)', color: '#fff', fontSize: 7, padding: '1px 5px', borderRadius: '0 0 4px 0', fontWeight: 700 }}>SHEET</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Auction Sheet Full-Screen Viewer ── */}
          {sheetViewer !== null && auctionSheets[sheetViewer] && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={() => setSheetViewer(null)}>
              <button style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer', zIndex: 10 }}>✕</button>
              {sheetViewer > 0 && <button style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer', width: 44, height: 44, borderRadius: '50%' }} onClick={e => { e.stopPropagation(); setSheetViewer(sheetViewer - 1); }}>‹</button>}
              {sheetViewer < auctionSheets.length - 1 && <button style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer', width: 44, height: 44, borderRadius: '50%' }} onClick={e => { e.stopPropagation(); setSheetViewer(sheetViewer + 1); }}>›</button>}
              <img src={auctionSheets[sheetViewer]} alt="Auction Sheet" style={{ maxWidth: '92vw', maxHeight: '90vh', objectFit: 'contain' }} onClick={e => e.stopPropagation()} />
              <div style={{ position: 'absolute', bottom: 16, color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>Auction Sheet {sheetViewer + 1} / {auctionSheets.length} — Click outside to close</div>
            </div>
          )}

          {/* ── SKU + Status row ── */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{t('sku')}</div>
              <div style={{ fontFamily: 'monospace', color: 'var(--cyan)', background: 'var(--bg3)', padding: '8px 12px', borderRadius: 6 }}>
                {item.sku}
              </div>
            </div>
            <div style={{ minWidth: 140 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>{t('status')}</div>
              {canEdit && !isYardRole ? (
                <select className="form-select" value={status} onChange={e => handleStatusChange(e.target.value)} style={{ width: '100%' }}>
                  {STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              ) : (
                <span className={`status status-${item.status}`}>{t(item.status)}</span>
              )}
            </div>
            <div style={{ minWidth: 140 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Destination Yard</div>
              {canEdit && !isYardRole ? (
                <select className="form-select" value={destinationYard} onChange={e => { setDestinationYard(e.target.value); setIsDirty(true); }} style={{ width: '100%' }}>
                  <option value="">— Not assigned —</option>
                  {getYardLocations().map(y => <option key={y.id} value={y.id}>{y.flag} {y.name}</option>)}
                </select>
              ) : (
                <div style={{ fontWeight: 600, fontSize: 13, padding: '8px 0' }}>{destinationYard ? `${getYardLocations().find(y => y.id === destinationYard)?.flag || '📍'} ${getYardLocations().find(y => y.id === destinationYard)?.name || destinationYard}` : '—'}</div>
              )}
            </div>
            {(isAdmin || isManager) && (
            <div style={{ minWidth: 140 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Destination Country</div>
              {isAdmin ? (
                <select className="form-select" value={destinationCountry} onChange={e => { setDestinationCountry(e.target.value); setIsDirty(true); }} style={{ width: '100%' }}>
                  <option value="">— Not assigned —</option>
                  {getDestinationCountries().map(c => <option key={c.id} value={c.id}>{c.flag} {c.name}</option>)}
                </select>
              ) : (
                <div style={{ fontWeight: 600, fontSize: 13, padding: '8px 0' }}>{destinationCountry ? `${getDestinationCountries().find(c => c.id === destinationCountry)?.flag || '🌍'} ${getDestinationCountries().find(c => c.id === destinationCountry)?.name || destinationCountry}` : '—'}</div>
              )}
            </div>
            )}
            {(isAdmin || isManager) && (
            <div style={{ minWidth: 140 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Cut Type</div>
              {isAdmin ? (
                <select className="form-select" value={unitCutType} onChange={e => { setUnitCutType(e.target.value); setIsDirty(true); }} style={{ width: '100%' }}>
                  <option value="">— Not assigned —</option>
                  <option value="cbu">CBU</option>
                  <option value="half_cut">Half Cut</option>
                  <option value="ckd">CKD</option>
                </select>
              ) : (
                <div style={{ fontWeight: 600, fontSize: 13, padding: '8px 0' }}>{unitCutType ? ({ cbu: 'CBU', half_cut: 'Half Cut', ckd: 'CKD' }[unitCutType] || unitCutType) : '—'}</div>
              )}
            </div>
            )}
            {item.current_zone && (() => {
              const target = resolveTargetZone(item);
              const targetLabel = target.status === 'ok' ? target.zone
                : target.status === 'no_yard' ? 'no yard'
                : target.status === 'no_country' ? 'awaiting country'
                : target.status === 'unmapped' ? 'unmapped'
                : target.status === 'movable_unknown' ? 'movable unknown'
                : '—';
              return (
                <div style={{ minWidth: 140 }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Zone</div>
                  <div style={{ fontWeight: 600, fontSize: 13, padding: '8px 0' }}>
                    {item.current_zone.toUpperCase()}
                    <span style={{ color: 'var(--text3)', fontWeight: 400, marginLeft: 8, fontSize: 11 }}>→ {targetLabel.toUpperCase()}</span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* ── Assign to Customer (staff, not yard roles) ── */}
          {canEdit && !isYardRole && (
            <div style={{ marginBottom: 16, padding: 14, background: 'var(--bg3)', borderRadius: 10, border: assignedTo ? '1px solid var(--blue)' : '1px solid var(--border)' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {t('detail_assign_customer')}
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  className="form-select"
                  value={assignedTo}
                  onChange={e => {
                    const val = e.target.value;
                    setAssignedTo(val);
                    if (val) {
                      setStatus('assigned');
                    } else {
                      setStatus('available');
                    }
                    setIsDirty(true);
                  }}
                  style={{ flex: 1, minWidth: 180 }}
                >
                  <option value="">— Not assigned —</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ''}</option>)}
                </select>
                {assignedTo && (
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ color: 'var(--red)', borderColor: 'var(--red)', flexShrink: 0 }}
                    onClick={() => {
                      setAssignedTo('');
                      setStatus('available');
                      setIsDirty(true);
                    }}
                  >
                    ✕ Unassign
                  </button>
                )}
              </div>
              {assignedTo && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--blue)' }}>
                  ✓ Assigned to {customers.find(c => c.id === assignedTo)?.name || 'customer'} — only they can see this unit
                </div>
              )}
            </div>
          )}

          {/* ── Specs grid ── */}
          <div className="detail-specs">
            {type === 'vehicles' && (
              <>
                {item.vehicleCategory && item.vehicleCategory !== 'passenger_car' && (
                  <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_category')}</div><div className="detail-spec-val" style={{ textTransform: 'capitalize' }}>{(item.vehicleCategory || '').replace(/_/g, ' ')}</div></div>
                )}
                {item.chassis && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_chassis')}</div><div className="detail-spec-val">{item.chassis}</div></div>}
                {item.year && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_year')}</div><div className="detail-spec-val">{item.year}</div></div>}
                {item.engineCode && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_engine_code')}</div><div className="detail-spec-val">{item.engineCode}</div></div>}
                {item.displacement && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_displacement')}</div><div className="detail-spec-val">{item.displacement}</div></div>}
                {item.fuel && <div className="detail-spec"><div className="detail-spec-lbl">Fuel</div><div className="detail-spec-val">{item.fuel}</div></div>}
                {item.mileage > 0 && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_mileage')}</div><div className="detail-spec-val">{item.mileage?.toLocaleString()} km</div></div>}
                {item.score && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_score')}</div><div className="detail-spec-val">{item.score}</div></div>}
                {item.grade && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_grade')}</div><div className="detail-spec-val">{item.grade}</div></div>}
                {item.color && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_color')}</div><div className="detail-spec-val">{item.color}</div></div>}
                {item.transmission && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_transmission')}</div><div className="detail-spec-val">{item.transmission}</div></div>}
                {item.driveSystem && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_drive')}</div><div className="detail-spec-val">{item.driveSystem}</div></div>}
                {item.steer && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_steering')}</div><div className="detail-spec-val">{item.steer}</div></div>}
                {item.seats && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_seats')}</div><div className="detail-spec-val">{item.seats}</div></div>}
                {item.country && <div className="detail-spec"><div className="detail-spec-lbl">{t('misc_country')}</div><div className="detail-spec-val">{item.country}</div></div>}
                {item.city && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_city')}</div><div className="detail-spec-val">{item.city}</div></div>}
                {item.location && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_location')}</div><div className="detail-spec-val">{item.location}</div></div>}
                {item.port && !item.country && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_port')}</div><div className="detail-spec-val">{item.port}</div></div>}

                {/* Extra specs from JSONB */}
                {item.extraSpecs && Object.entries(item.extraSpecs).filter(([, v]) => v !== '' && v !== false && v !== null && v !== undefined).map(([key, val]) => (
                  <div key={key} className="detail-spec">
                    <div className="detail-spec-lbl">{key.replace(/_/g, ' ')}</div>
                    <div className="detail-spec-val">{typeof val === 'boolean' ? (val ? 'Yes' : 'No') : String(val).replace(/_/g, ' ')}</div>
                  </div>
                ))}
              </>
            )}
            {type === 'engines' && (
              <>
                {item.manufacturer && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_manufacturer')}</div><div className="detail-spec-val">{item.manufacturer}</div></div>}
                {item.displacement && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_displacement')}</div><div className="detail-spec-val">{item.displacement}</div></div>}
                {item.fuel && <div className="detail-spec"><div className="detail-spec-lbl">Fuel</div><div className="detail-spec-val">{item.fuel}</div></div>}
                {item.mileage > 0 && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_mileage')}</div><div className="detail-spec-val">{item.mileage?.toLocaleString()} km</div></div>}
                {item.condition && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_condition')}</div><div className="detail-spec-val">{item.condition}</div></div>}
                {item.make && <div className="detail-spec"><div className="detail-spec-lbl">Source</div><div className="detail-spec-val">{[item.make, item.model].filter(Boolean).join(' ')}</div></div>}
                {item.chassis && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_chassis')}</div><div className="detail-spec-val">{item.chassis}</div></div>}
                {item.country && <div className="detail-spec"><div className="detail-spec-lbl">{t('misc_country')}</div><div className="detail-spec-val">{item.country}</div></div>}
              </>
            )}
            {type === 'parts' && (
              <>
                {(item.category || cutTypeName) && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_category')}</div><div className="detail-spec-val">{cutTypeName || (item.category === 'Cut Types' ? (CUT_TYPES.find(c => c.id === item.cutType)?.name || item.partName?.split('—').pop()?.trim() || item.category) : item.category)}</div></div>}
                {item.description && <div className="detail-spec"><div className="detail-spec-lbl">Description</div><div className="detail-spec-val">{item.description}</div></div>}
                {item.condition && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_condition')}</div><div className="detail-spec-val">{item.condition}</div></div>}
                {item.fuel && <div className="detail-spec"><div className="detail-spec-lbl">Fuel</div><div className="detail-spec-val">{item.fuel}</div></div>}
                {item.mileage > 0 && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_mileage')}</div><div className="detail-spec-val">{item.mileage?.toLocaleString()} km</div></div>}
                {item.year && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_year')}</div><div className="detail-spec-val">{item.year}</div></div>}
                {item.make && <div className="detail-spec"><div className="detail-spec-lbl">Source</div><div className="detail-spec-val">{[item.make, item.model].filter(Boolean).join(' ')}</div></div>}
                {item.chassis && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_chassis')}</div><div className="detail-spec-val">{item.chassis}</div></div>}
                {item.fitment && item.fitment !== item.chassis && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_fitment')}</div><div className="detail-spec-val">{item.fitment}</div></div>}
                {item.quantity > 1 && <div className="detail-spec"><div className="detail-spec-lbl">{t('detail_quantity')}</div><div className="detail-spec-val">{item.quantity}</div></div>}
                {item.country && <div className="detail-spec"><div className="detail-spec-lbl">{t('misc_country')}</div><div className="detail-spec-val">{item.country}</div></div>}
                {/* Cut details */}
                {item.cut_details && Object.values(item.cut_details).some(v => v) && (
                  <div style={{ gridColumn: 'span 2', marginTop: 8, padding: 10, background: 'var(--bg3)', borderRadius: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>Cut Details</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', fontSize: 12 }}>
                      {item.cut_details.doors_included && <span>✅ {item.cut_details.doors_quantity || ''} Doors</span>}
                      {item.cut_details.back_trunk && <span>✅ Back trunk</span>}
                      {item.cut_details.brake_lights && <span>✅ Brake lights</span>}
                      {item.cut_details.back_differential && <span>✅ Back differential</span>}
                      {item.cut_details.back_shocks && <span>✅ Back shocks</span>}
                      {item.cut_details.back_bumper && <span>✅ Back bumper</span>}
                      {item.cut_details.front_bumper && <span>✅ Front bumper</span>}
                      {item.cut_details.front_shocks && <span>✅ Front shocks</span>}
                      {item.cut_details.radiator && <span>✅ Radiator</span>}
                      {item.cut_details.ac_compressor && <span>✅ AC compressor</span>}
                      {item.cut_details.steering_rack && <span>✅ Steering rack</span>}
                      {item.cut_details.dashboard && <span>✅ Dashboard</span>}
                      {item.cut_details.seats && <span>✅ Seats</span>}
                      {item.cut_details.with_catalyst === true && <span>✅ With catalyst</span>}
                      {item.cut_details.with_catalyst === false && <span>❌ Without catalyst</span>}
                      {item.cut_details.additional_parts && <span style={{ color: 'var(--text3)' }}>📝 {item.cut_details.additional_parts}</span>}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Assigned label (read-only for non-staff) ── */}
          {!canEdit && item.assignedTo && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{t('detail_assigned_to')}</div>
              <div style={{ fontWeight: 600 }}>{customers.find(c => c.id === item.assignedTo)?.name || item.assignedTo}</div>
            </div>
          )}

          {cutTypeName && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{t('cutType')}</div>
              <div style={{ fontWeight: 600 }}>{cutTypeName}</div>
            </div>
          )}

          {showPrices && (item.costJPY > 0 || item.priceJPY > 0) && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>Purchase Price</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--green)' }}>{formatPrice(item.costJPY || item.priceJPY, currency)}</div>
            </div>
          )}

          {/* Dismantled badge + revert option */}
          {(item.dismantled || item.status === 'dismantled' || item.status === 'converted') && (
            <div style={{ marginTop: 14, padding: '10px 14px', background: 'rgba(139,148,158,0.1)', borderRadius: 8, border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)' }}>{item.status === 'converted' ? 'CONVERTED' : 'DISMANTLED'}</div>
                  {item.dismantled_at && <div style={{ fontSize: 10, color: 'var(--text3)' }}>{new Date(item.dismantled_at).toLocaleDateString()}</div>}
                </div>
                {(user?.role === 'admin' || user?.role === 'head_admin') && (
                  <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => {
                    // Check if any child parts have been sold
                    const allParts = DB.get('spareParts') || [];
                    const childIds = Array.isArray(item.child_part_ids) ? item.child_part_ids : [];
                    const children = allParts.filter(p => childIds.includes(p.id) || p.parentId === item.id || p.parent_id === item.id || p.sourceVehicle === item.id);
                    const soldChildren = children.filter(c => ['sold', 'sold_awaiting_payment', 'assigned', 'shipped', 'delivered'].includes(c.status));

                    if (soldChildren.length > 0) {
                      alert(`Cannot revert — ${soldChildren.length} part${soldChildren.length !== 1 ? 's have' : ' has'} been sold.`);
                      return;
                    }

                    if (!window.confirm(`Revert "${item.make || ''} ${item.model || ''}" to full unit?\n\nThis will delete ${children.length} created part${children.length !== 1 ? 's' : ''} and restore the vehicle to available.`)) return;

                    // Delete child parts
                    if (children.length > 0) {
                      DB.set('spareParts', allParts.filter(p => !children.some(c => c.id === p.id)));
                    }

                    // Restore vehicle
                    centralUpdateStatus(item.id, 'vehicle', 'available', {
                      updatedBy: user?.name,
                      extraFields: { dismantled: false, dismantled_at: null, child_part_ids: [], convertedTo: null, convertedToId: null }
                    });

                    alert('✅ Vehicle restored to full unit.');
                    onClose();
                  }}>
                    🔄 Revert to Full Unit
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── FAMILY TREE — Origin + Siblings + Children ── */}
          {(() => {
            const allVehicles = DB.get('vehicles') || [];
            const allParts = DB.get('spareParts') || [];
            const allUnits = [...allVehicles, ...allParts];
            const cutPlans = DB.get('cutPlans') || [];

            // Find parent
            const parentId = item.parent_unit_id || item.parentId || item.parent_id || item.sourceVehicle || item.convertedFrom;
            const parent = parentId ? allUnits.find(u => u.id === parentId) : null;

            // Find root vehicle (for multi-level cuts)
            const rootId = item.root_vehicle_id || parentId;
            const root = rootId && rootId !== parentId ? allVehicles.find(v => v.id === rootId) : null;

            // Find siblings (other parts from same parent)
            const siblings = parentId ? allParts.filter(p => p.id !== item.id && (p.parent_unit_id === parentId || p.parentId === parentId || p.parent_id === parentId)) : [];

            // Find children (parts created from THIS unit)
            const childIds = Array.isArray(item.child_part_ids) ? item.child_part_ids : [];
            const children = allUnits.filter(u => childIds.includes(u.id) || u.parent_unit_id === item.id || u.parentId === item.id || u.parent_id === item.id);

            // Find cut plan
            const cutPlan = item.cut_plan_id ? cutPlans.find(cp => cp.id === item.cut_plan_id) : null;

            const getName = (u) => u?.make ? `${u.make} ${u.model || ''}`.trim() : u?.partName || u?.engineName || 'Unknown';
            const hasOrigin = parent || root || item.created_from_cut;
            const hasChildren = children.length > 0;
            const hasSiblings = siblings.length > 0;

            if (!hasOrigin && !hasChildren) return null;

            return (
              <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--bg3)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                  Unit Family Tree
                </div>

                {/* Root vehicle */}
                {root && root.id !== parent?.id && (
                  <div style={{ fontSize: 11, padding: '4px 0', color: 'var(--text2)' }}>
                    🚗 <strong>{getName(root)}</strong> <span style={{ fontFamily: 'monospace', color: 'var(--cyan)', fontSize: 9 }}>{root.sku}</span>
                    <span style={{ color: 'var(--text3)', marginLeft: 6 }}>{(root.status || '').replace(/_/g, ' ')}</span>
                  </div>
                )}

                {/* Parent */}
                {parent && (
                  <div style={{ fontSize: 11, padding: '4px 0', paddingLeft: root && root.id !== parent.id ? 16 : 0 }}>
                    <span style={{ color: 'var(--text3)' }}>{root && root.id !== parent.id ? '├── ' : ''}</span>
                    {parent.make ? '🚗' : '🔧'} <strong>{getName(parent)}</strong> <span style={{ fontFamily: 'monospace', color: 'var(--cyan)', fontSize: 9 }}>{parent.sku}</span>
                    <span style={{ color: 'var(--text3)', marginLeft: 6, fontSize: 10 }}>{(parent.status || '').replace(/_/g, ' ')}</span>
                    {cutPlan && <span style={{ fontSize: 9, color: 'var(--text3)', marginLeft: 6 }}>Plan: #CP-{String(cutPlan.plan_number || '').padStart(3, '0')}</span>}
                  </div>
                )}

                {/* This item highlighted */}
                {parent && (
                  <div style={{ fontSize: 11, padding: '4px 0', paddingLeft: (root && root.id !== parent?.id ? 32 : 16), fontWeight: 700, color: 'var(--blue)' }}>
                    ├── 🔧 {getName(item)} <span style={{ fontFamily: 'monospace', fontSize: 9 }}>{item.sku}</span> ← this
                  </div>
                )}

                {/* Siblings */}
                {hasSiblings && siblings.map((s, i) => {
                  const cust = s.assignedTo ? (customers.find(c => c.id === s.assignedTo)?.name || '') : '';
                  return (
                    <div key={s.id} style={{ fontSize: 11, padding: '3px 0', paddingLeft: (root && root.id !== parent?.id ? 32 : 16), color: 'var(--text2)' }}>
                      {i === siblings.length - 1 ? '└── ' : '├── '}
                      🔧 {s.partName || s.category || getName(s)}
                      <span style={{ fontFamily: 'monospace', color: 'var(--cyan)', fontSize: 9, marginLeft: 4 }}>{s.sku}</span>
                      {cust && <span style={{ color: 'var(--blue)', fontSize: 10, marginLeft: 4 }}>→ {cust}</span>}
                      <span style={{ color: s.status === 'sold' || s.status === 'delivered' ? 'var(--green)' : 'var(--text3)', fontSize: 9, marginLeft: 4 }}>
                        {s.is_scrap ? 'scrapped' : (s.status || '').replace(/_/g, ' ')}
                      </span>
                    </div>
                  );
                })}

                {/* Children (if this unit was cut) */}
                {hasChildren && (
                  <>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 8, marginBottom: 4, textTransform: 'uppercase' }}>Parts from this unit ({children.length})</div>
                    <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                      {children.map((c, i) => {
                        const cust = c.assignedTo ? (customers.find(cu => cu.id === c.assignedTo)?.name || '') : '';
                        return (
                          <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', fontSize: 11, borderBottom: '1px solid var(--border)' }}>
                            <span style={{ color: 'var(--text3)' }}>{i === children.length - 1 ? '└──' : '├──'}</span>
                            <span style={{ fontFamily: 'monospace', color: 'var(--cyan)', fontSize: 9 }}>{c.sku}</span>
                            <span style={{ flex: 1 }}>{c.partName || c.category || getName(c)}</span>
                            {cust && <span style={{ color: 'var(--blue)', fontSize: 10 }}>→ {cust}</span>}
                            <span style={{ fontSize: 9, color: c.status === 'sold' || c.status === 'delivered' ? 'var(--green)' : 'var(--text3)' }}>
                              {(c.status || '').replace(/_/g, ' ')}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {showPrices && (() => {
                      const soldVal = children.filter(c => c.assignedTo).reduce((s, c) => s + (c.costJPY || c.priceJPY || 0), 0);
                      const stockVal = children.filter(c => !c.assignedTo).reduce((s, c) => s + (c.costJPY || c.priceJPY || 0), 0);
                      return (
                        <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border)', fontSize: 11 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text3)' }}>Sold/assigned</span><span style={{ color: 'var(--green)' }}>{formatPrice(soldVal, currency)}</span></div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text3)' }}>In stock</span><span>{formatPrice(stockVal, currency)}</span></div>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            );
          })()}

          {/* ── Partial Sell — bulk quantity items ── */}
          {canEdit && (item.quantity || 1) > 1 && item.status !== 'sold' && (() => {
            const totalQty = item.quantity || 1;
            const soldQty = item.sold_quantity || 0;
            const remaining = totalQty - soldQty;
            const maxSell = Math.min(partialSellQty, remaining);

            const handlePartialSell = () => {
              if (maxSell < 1 || maxSell > remaining) return;
              const sellDate = new Date().toISOString();
              const newSoldQty = soldQty + maxSell;
              const newRemaining = totalQty - newSoldQty;
              const sale = { qty: maxSell, date: sellDate, soldBy: user?.name || 'Staff' };
              const updatedSales = [...(item.partial_sales || []), sale];
              const updated = {
                ...item,
                sold_quantity: newSoldQty,
                partial_sales: updatedSales,
              };
              if (newRemaining <= 0) {
                // All units sold — mark as sold
                updated.status = 'sold';
                centralUpdateStatus(item.id, detectUnitType(item), 'sold', { updatedBy: user?.name });
              }
              onUpdate(updated);
              setPartialSellQty(1);
            };

            return (
              <div style={{ marginTop: 16, padding: 14, background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
                  Bulk Quantity — Partial Sell
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--cyan)' }}>{remaining}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>remaining</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--green)' }}>{soldQty}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>sold</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text2)' }}>{totalQty}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>total</div>
                  </div>
                </div>
                {remaining > 0 && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>Sell</label>
                    <input
                      type="number"
                      min={1}
                      max={remaining}
                      value={partialSellQty}
                      onChange={e => setPartialSellQty(Math.max(1, Math.min(remaining, parseInt(e.target.value) || 1)))}
                      style={{ width: 64, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text1)', fontSize: 14, fontWeight: 700, textAlign: 'center' }}
                    />
                    <label style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>of {remaining} units</label>
                    <button
                      className="btn btn-primary"
                      style={{ fontSize: 12, padding: '6px 16px' }}
                      onClick={handlePartialSell}
                    >
                      Sell {maxSell} unit{maxSell !== 1 ? 's' : ''}
                    </button>
                  </div>
                )}
                {/* Partial sales history */}
                {(item.partial_sales || []).length > 0 && (
                  <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>Sale History</div>
                    {(item.partial_sales || []).map((sale, i) => (
                      <div key={i} style={{ fontSize: 11, color: 'var(--text2)', padding: '2px 0', display: 'flex', gap: 8 }}>
                        <span style={{ color: 'var(--green)', fontWeight: 600 }}>-{sale.qty}</span>
                        <span>{new Date(sale.date).toLocaleDateString()}</span>
                        {sale.soldBy && <span style={{ color: 'var(--text3)' }}>by {sale.soldBy}</span>}
                        {sale.customer && <span style={{ color: 'var(--blue)' }}>to {sale.customer}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Notes */}
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t('detail_notes')}</div>
            {canEdit ? (
              <textarea className="form-input" rows={3} placeholder="Add notes or comments about this unit..."
                value={notes} onChange={e => { setNotes(e.target.value); setIsDirty(true); }}
                style={{ resize: 'vertical', minHeight: 60, width: '100%', boxSizing: 'border-box' }} />
            ) : (
              <div style={{ color: notes ? 'var(--text)' : 'var(--text3)', fontSize: 13, fontStyle: notes ? 'normal' : 'italic' }}>
                {notes || 'No notes'}
              </div>
            )}
          </div>

          {/* ── QR + Actions ── */}
          <div style={{ marginTop: 20, display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <QRBox sku={item.sku} />
            <div style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {canEdit && isDirty && (
                <button className="btn btn-success" onClick={handleSave}><CheckIcon />{t('btn_save_changes')}</button>
              )}
              {/* Convert vehicle to part — admin only, any vehicle */}
              {type === 'vehicles' && !isCustomer && (user?.role === 'admin' || user?.role === 'head_admin') && item.status !== 'converted' && (
                <button className="btn btn-secondary" style={{ fontSize: 11 }} onClick={() => setShowConvert(true)}>
                  🔄 Convert to Part
                </button>
              )}
              {isCustomer && item.assignedTo === user?.id && item.status !== 'arrived' && onUpdate && (
                <button className="btn btn-success" onClick={() => { centralUpdateStatus(item.id, detectUnitType(item), 'arrived', { updatedBy: user?.name }); onUpdate({ ...item, status: 'arrived' }); }}>
                  <CheckIcon />{t('cont_mark_received')}
                </button>
              )}
              {isCustomer && item.assignedTo === user?.id && onUpdate && (
                <button className="btn btn-secondary" style={{ color: 'var(--red)', borderColor: 'var(--red)' }}
                  onClick={() => { if (window.confirm('Return this unit to available stock?')) { centralUpdateStatus(item.id, detectUnitType(item), 'available', { updatedBy: user?.name }); onUpdate({ ...item, status: 'available', assignedTo: '' }); onClose(); } }}>
                  ✕ Cancel / Return to Stock
                </button>
              )}
              {isCustomer && onRequestParts && item.status !== 'sold' && (type === 'vehicles' || (item.category && ['Half Cut', 'Nose Cut', 'CKD', 'SKD', 'Roof Cut', 'Half Cut (H/CUT)', 'CBU', 'CBU (Complete Built Unit)', 'CKD W/O Engine'].includes(item.category))) && (
                <button className="btn btn-primary" onClick={() => { onRequestParts(item); onClose(); }}>
                  {type === 'vehicles' ? 'Request Parts from This Car' : `Parts from This ${item.category || 'Unit'}`}
                </button>
              )}
              {isCustomer && item.status === 'arrived' && (
                <div style={{ background: 'rgba(63,185,80,0.12)', border: '1px solid rgba(63,185,80,0.3)', borderRadius: 8, padding: '10px 14px', color: 'var(--green)', fontSize: 13, textAlign: 'center', fontWeight: 600 }}>
                  ✓ Arrived at Your Yard
                </div>
              )}
              {images.length > 0 && (
                <button className="btn btn-secondary" onClick={async (e) => {
                  const btn = e.currentTarget;
                  const origText = btn.textContent;
                  btn.disabled = true;
                  btn.textContent = 'Preparing...';
                  try {
                    const zip = new JSZip();
                    for (let i = 0; i < images.length; i++) {
                      btn.textContent = `Fetching ${i + 1}/${images.length}...`;
                      let url = images[i];
                      if (url.includes('supabase') && !url.startsWith('data:')) {
                        url = url + (url.includes('?') ? '&' : '?') + 'download=true';
                      }
                      const resp = await fetch(url, { mode: 'cors' });
                      const blob = await resp.blob();
                      zip.file(`${item.sku || 'photo'}-${i + 1}.jpg`, blob);
                    }
                    btn.textContent = 'Creating ZIP...';
                    const zipBlob = await zip.generateAsync({ type: 'blob' });
                    const blobUrl = URL.createObjectURL(zipBlob);
                    const a = document.createElement('a');
                    a.href = blobUrl;
                    a.download = `${item.sku || 'photos'}-images.zip`;
                    a.style.display = 'none';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(blobUrl);
                  } catch {
                    alert('Could not download images. Please try again.');
                  }
                  btn.textContent = origText;
                  btn.disabled = false;
                }}>
                  ⬇ Download Photos ({images.length})
                </button>
              )}
              {!isCustomer && <button className="btn btn-primary" onClick={() => setShowPrintLabel(true)}><PrintIcon /> Print QR Code</button>}
              <button className="btn btn-whatsapp" onClick={openWhatsApp}><WhatsAppIcon />{t('whatsAppInquiry')}</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* ── Lightbox ── */}

    {lightbox && images.length > 0 && (
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 2000,
          background: 'rgba(0,0,0,0.95)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        onClick={() => setLightbox(false)}
      >
        {/* Close */}
        <button
          onClick={() => setLightbox(false)}
          style={{
            position: 'absolute', top: 16, right: 20,
            background: 'none', border: 'none', color: '#fff',
            fontSize: 36, cursor: 'pointer', lineHeight: 1, zIndex: 1,
          }}
        >×</button>

        {/* Counter */}
        {images.length > 1 && (
          <div style={{
            position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)',
            color: '#fff', fontSize: 13, opacity: 0.7,
          }}>
            {activeImg + 1} / {images.length}
          </div>
        )}

        {/* Image */}
        <img
          src={images[activeImg]}
          alt={name}
          onClick={e => e.stopPropagation()}
          style={{ maxWidth: '92vw', maxHeight: '88vh', objectFit: 'contain', borderRadius: 8 }}
        />

        {/* Prev / Next */}
        {images.length > 1 && (
          <>
            <button
              onClick={e => { e.stopPropagation(); setActiveImg(i => (i - 1 + images.length) % images.length); }}
              style={{
                position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
                fontSize: 32, width: 52, height: 52, borderRadius: '50%',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >‹</button>
            <button
              onClick={e => { e.stopPropagation(); setActiveImg(i => (i + 1) % images.length); }}
              style={{
                position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
                fontSize: 32, width: 52, height: 52, borderRadius: '50%',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >›</button>
          </>
        )}
      </div>
    )}
    {/* Convert to Part modal */}
    {showConvert && (
      <div className="modal-overlay" style={{ zIndex: 1200 }} onClick={() => setShowConvert(false)}>
        <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3 className="modal-title">Convert Vehicle to Part</h3>
            <button className="modal-close" onClick={() => setShowConvert(false)}>×</button>
          </div>
          <div className="modal-body">
            <div style={{ fontSize: 13, marginBottom: 12 }}>
              <strong>{item.make} {item.model}</strong> ({item.sku})
            </div>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="form-label">Part Type</label>
              <select className="form-select" value={convertType} onChange={e => setConvertType(e.target.value)}>
                {CUT_OPTIONS.map(pt => <option key={pt.id} value={pt.id}>{pt.display}</option>)}
              </select>
            </div>
            <div style={{ fontSize: 11, color: 'var(--yellow)', padding: '10px 12px', background: 'rgba(210,153,34,0.08)', borderRadius: 8, marginBottom: 14 }}>
              The vehicle record will be marked as 'converted' and hidden from inventory. All data, images, and customer links will transfer to the new part.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setShowConvert(false)}>Cancel</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => {
                const ptEntry = getPartType(convertType) || resolvePartTypeEntry(item);
                const cutName = ptEntry.display;
                const partsList = DB.get('spareParts') || [];
                const makeCode = (item.make || 'UNK').substring(0, 3).toUpperCase();
                const d = new Date();
                const dateCode = `${String(d.getFullYear()).slice(2)}${String(d.getMonth() + 1).padStart(2, '0')}`;
                const newSku = `PRT-${makeCode}-${dateCode}-${String(partsList.length + 1).padStart(3, '0')}`;
                const newId = DB.genId('PRT');

                const newPart = {
                  id: newId, sku: newSku,
                  partName: `${item.make || ''} ${item.model || ''} — ${cutName}`.trim(),
                  partType: ptEntry.id, category: cutName, vehicleClass: 'car',
                  condition: item.condition || 'Used',
                  make: item.make, model: item.model, year: item.year,
                  chassis: item.chassis, fitment: item.chassis,
                  country: item.country, city: item.city,
                  images: item.images || [], notes: `Converted from vehicle ${item.sku}`,
                  pricing: item.pricing, costJPY: item.costJPY,
                  purchase_supplier: item.purchase_supplier, purchase_source: item.purchase_source,
                  destination_yard: item.destination_yard || null,
                  status: item.status === 'converted' ? 'available' : (item.status || 'available'),
                  assignedTo: item.assignedTo || null,
                  quantity: 1, daysInStock: item.daysInStock || 0,
                  createdAt: new Date().toISOString().split('T')[0],
                  convertedFrom: item.id, convertedFromSku: item.sku,
                };
                DB.set('spareParts', [...partsList, newPart]);

                centralUpdateStatus(item.id, 'vehicle', 'converted', { updatedBy: user?.name, extraFields: { convertedTo: newSku, convertedToId: newId } });

                // Update customer_stock entries
                const stock = DB.get('customerStock') || [];
                DB.set('customerStock', stock.map(s => s.unitId === item.id && s.unitType === 'vehicle' ? { ...s, unitId: newId, unitType: 'spare_part' } : s));

                // Update selection orders
                const orders = DB.get('selectionOrders') || [];
                DB.set('selectionOrders', orders.map(o => ({
                  ...o,
                  units: (o.units || []).map(u => u.unit_id === item.id ? { ...u, unit_id: newId, unit_type: 'spare_part', unit_sku: newSku } : u),
                })));

                // Update yard bids
                const bids = DB.get('yardBids') || [];
                DB.setAsync('yardBids', bids.map(b => b.unit_id === item.id ? { ...b, unit_id: newId, unit_sku: newSku, unit_type: 'spare_part' } : b));

                setShowConvert(false);
                alert(`✅ Converted!\n\nOld: ${item.sku} (Vehicle) → marked converted\nNew: ${newSku} (${cutName}) → active\n\nAll data transferred.`);
                onClose();
              }}>🔄 Convert</button>
            </div>
          </div>
        </div>
      </div>
    )}
    {showPrintLabel && <PrintLabelModal unit={item} onClose={() => setShowPrintLabel(false)} />}
    </>
  );
}

export default DetailModal;
